import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
// Zoom icons (in/out): plain plus and minus
const IconZoomIn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconZoomOut = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// View in Space icon
const IconViewInSpace = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ clientX: 0, clientY: 0, originX: 50, originY: 50 });
  const ZOOM_STEP = 0.5;
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, onViewInSpace } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const handleViewInSpace = (e, image) => {
    e.stopPropagation();
    if (onViewInSpace) {
      const imageUrl = image?.attributes?.variants?.['scaled-large']?.url ||
                       image?.attributes?.variants?.['scaled-medium']?.url;
      onViewInSpace(imageUrl);
    }
  };

  const beginDrag = (clientX, clientY, rect) => {
    if (zoomLevel <= 1 || !rect) return;
    dragStartRef.current = {
      clientX,
      clientY,
      originX: zoomOrigin.x,
      originY: zoomOrigin.y,
      rect,
    };
    setIsDragging(true);
  };

  // Mouse: start the drag from the element. Subsequent move/up are tracked on document.
  const handleMouseDown = e => {
    if (zoomLevel <= 1) return;
    e.preventDefault();
    beginDrag(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
  };

  // Touch start: React's onTouchStart is fine here (no preventDefault needed yet).
  const handleTouchStart = e => {
    if (zoomLevel <= 1 || e.touches.length !== 1) return;
    const t = e.touches[0];
    beginDrag(t.clientX, t.clientY, e.currentTarget.getBoundingClientRect());
  };

  // While dragging, listen for move/end on the document so the gesture keeps
  // working even when the finger/cursor leaves the original slide element.
  useEffect(() => {
    if (!isDragging) return;
    const start = dragStartRef.current;
    if (!start || !start.rect) return;

    const applyDelta = (clientX, clientY) => {
      const dx = ((clientX - start.clientX) / start.rect.width) * 100;
      const dy = ((clientY - start.clientY) / start.rect.height) * 100;
      const nextX = start.originX - dx;
      const nextY = start.originY - dy;
      setZoomOrigin({
        x: Math.max(0, Math.min(100, nextX)),
        y: Math.max(0, Math.min(100, nextY)),
      });
    };

    const onMouseMove = e => applyDelta(e.clientX, e.clientY);
    const onTouchMove = e => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      const t = e.touches[0];
      applyDelta(t.clientX, t.clientY);
    };
    const stop = () => setIsDragging(false);

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', stop);
    document.addEventListener('touchcancel', stop);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stop);
      document.removeEventListener('touchcancel', stop);
    };
  }, [isDragging]);

  const renderItem = item => {
    const isZoomedIn = zoomLevel > 1;
    const zoomedStyle = {
      transform: `scale(${zoomLevel})`,
      transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
      cursor: isZoomedIn ? (isDragging ? 'grabbing' : 'grab') : 'default',
      transition: isDragging ? 'none' : 'transform 0.15s ease-out',
      touchAction: isZoomedIn ? 'none' : 'auto',
    };

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div
          className={css.itemCentering}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={zoomedStyle}
        >
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
          {onViewInSpace && !isFullscreen && zoomLevel <= 1 && (
            <button
              type="button"
              className={css.viewInSpaceButton}
              onClick={(e) => handleViewInSpace(e, item.image)}
            >
              <IconViewInSpace />
              <span><FormattedMessage id="ListingPage.viewInYourSpace" /></span>
            </button>
          )}
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div className={css.thumbInner}>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
    setZoomLevel(1);
    setZoomOrigin({ x: 50, y: 50 });
  };

  const onSlide = () => {
    // Reset zoom when navigating between images
    setZoomLevel(1);
    setZoomOrigin({ x: 50, y: 50 });
  };

  const handleZoomIn = () => {
    setZoomLevel(z => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  };
  const handleZoomOut = () => {
    setZoomLevel(z => {
      const next = Math.max(ZOOM_MIN, z - ZOOM_STEP);
      if (next === ZOOM_MIN) setZoomOrigin({ x: 50, y: 50 });
      return next;
    });
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  const renderCustomControls = () => {
    // Toolbar is shown in fullscreen on any device, and inline on mobile
    // (CSS controls visibility on desktop inline).
    const toolsClass = isFullscreen
      ? `${css.zoomTools} ${css.zoomToolsFullscreen}`
      : css.zoomTools;
    return (
      <div className={toolsClass}>
        <button
          type="button"
          className={css.zoomButton}
          onClick={handleZoomIn}
          disabled={zoomLevel >= ZOOM_MAX}
          title={intl.formatMessage({ id: 'ListingImageGallery.zoomIn' })}
        >
          <IconZoomIn />
        </button>
        <button
          type="button"
          className={css.zoomButton}
          onClick={handleZoomOut}
          disabled={zoomLevel <= ZOOM_MIN}
          title={intl.formatMessage({ id: 'ListingImageGallery.zoomOut' })}
        >
          <IconZoomOut />
        </button>
      </div>
    );
  };

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      renderCustomControls={renderCustomControls}
      onScreenChange={onScreenChange}
      onSlide={onSlide}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      disableSwipe={zoomLevel > 1}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
